import React, { useState, useEffect } from "react";
import { getAppResources, API_URL } from "../../services/api.js";
import { IoIosArrowDown, IoMdClose } from "react-icons/io";
import toast from "react-hot-toast";
import { FiRefreshCw } from 'react-icons/fi'; // Import refresh icon
import JiraSynchronizationModal from "./JiraSynchronizationModal";

const JiraResourceModal = ({ appName, userGoogleId, onClose }) => {
    const [resources, setResources] = useState([]);
    const [selectedResource, setSelectedResource] = useState(null);
    const [selectedProjectKeys, setSelectedProjectKeys] = useState(new Set());
    const [expandedResourceId, setExpandedResourceId] = useState(null);
    const [loading, setLoading] = useState(true);
    const [confirmLoading, setConfirmLoading] = useState(false);
    const [isSyncModalOpen, setIsSyncModalOpen] = useState(false);
    const [isRefreshing, setIsRefreshing] = useState(false); // State for refresh button
    const [lastRefreshed, setLastRefreshed] = useState(null);

    const processFetchedData = (data) => {
        setResources(data.resources || []);
        if (data.selected_resource) {
            const resource = data.selected_resource;
            setSelectedResource(resource);
            setExpandedResourceId(resource.id);
            const initialKeys = new Set(resource.selected_projects?.map(p => p.key) || []);
            setSelectedProjectKeys(initialKeys);
        }
        setLastRefreshed(data.timestamp);
    };

    useEffect(() => {
        const fetchResources = async () => {
            try {
                setLoading(true);
                const response = await getAppResources(appName, userGoogleId);
                // setResources(response.resources || []);

                // if (response.selected_resource) {
                //     const resource = response.selected_resource;
                //     setSelectedResource(resource);
                //     setExpandedResourceId(resource.id);
                //     const initialKeys = new Set(resource.selected_projects?.map(p => p.key) || []);
                //     setSelectedProjectKeys(initialKeys);
                // }
                processFetchedData(response);
            } catch (error) {
                toast.error("Failed to fetch Jira resources.");
            } finally {
                setLoading(false);
            }
        };
        fetchResources();
    }, [appName, userGoogleId]);

    const handleRefresh = async () => {
        setIsRefreshing(true);
        toast.loading("Refreshing Jira resources...");
        try {
            const res = await fetch(`${API_URL}/auth/jira/resources/refresh`, {
                method: 'POST',
                headers: { Authorization: `Bearer ${userGoogleId}` },
                credentials: "include"
            });
            const data = await res.json();
            if (res.status !== 200) throw new Error(data.detail || "Failed to refresh");

            processFetchedData(data); // Process the fresh data returned by the endpoint
            toast.dismiss();
            toast.success("Jira resources refreshed!");
        } catch (err) {
            toast.dismiss();
            toast.error(err.message);
        } finally {
            setIsRefreshing(false);
        }
    };

    const handleToggleProject = (projectKey) => {
        setSelectedProjectKeys(prevKeys => {
            const newKeys = new Set(prevKeys);
            newKeys.has(projectKey) ? newKeys.delete(projectKey) : newKeys.add(projectKey);
            return newKeys;
        });
    };

    const handleSetResource = (resource) => {
        if (selectedResource?.id !== resource.id) {
            setSelectedResource(resource);
            setSelectedProjectKeys(new Set());
            setExpandedResourceId(resource.id);
        }
    };

    const handleToggleExpand = (resourceId) => {
        setExpandedResourceId(prevId => prevId === resourceId ? null : resourceId);
    };

    // --- CHANGE 1: Logic for the "Select All" checkbox ---
    const handleSelectAll = () => {
        if (!selectedResource) return;

        const allProjectKeys = selectedResource.projects.map(p => p.key);

        // If all are already selected, deselect all. Otherwise, select all.
        if (selectedProjectKeys.size === allProjectKeys.length) {
            setSelectedProjectKeys(new Set());
        } else {
            setSelectedProjectKeys(new Set(allProjectKeys));
        }
    };

    const handleConfirmSelection = async () => {
        if (!selectedResource) {
            toast.error("Please select a resource before confirming.");
            return;
        }
        setConfirmLoading(true);
        try {
            const body = {
                selected_resource_id: selectedResource.id,
                project_keys: Array.from(selectedProjectKeys),
            };
            const response = await fetch(`${API_URL}/auth/jira/resources/select`, {
                method: "POST",
                headers: { "Content-Type": "application/json", Authorization: `Bearer ${userGoogleId}` },
                body: JSON.stringify(body),
                credentials: "include"
            });
            if (!response.ok) throw new Error("Failed to update selection.");
            toast.success("Jira configuration saved!");
            onClose();
        } catch (error) {
            toast.error(error.message);
        } finally {
            setConfirmLoading(false);
        }
    };

    return (
        <>
            <div className="fixed inset-0 bg-black bg-opacity-60 flex justify-center items-center z-50 p-4">
                <div className="bg-2 border border-zinc-700 w-full max-w-xl max-h-[90%] rounded-lg p-6 flex flex-col">
                    <div className="flex justify-between items-start mb-4">
                        <div>
                            <h2 className="text-lg font-semibold text-zinc-200">Jira Configuration</h2>
                            {lastRefreshed && !loading && (
                                <p className="text-xs text-zinc-400">
                                    List updated:{' '}
                                    {(() => {
                                        if (!lastRefreshed) return 'N/A';

                                        let date = new Date(lastRefreshed);
                                        // If invalid, try assuming UTC
                                        if (isNaN(date)) date = new Date(lastRefreshed + 'Z');
                                        if (isNaN(date)) return 'Invalid date';

                                        return date.toLocaleString(undefined, {
                                            year: 'numeric',
                                            month: 'short',
                                            day: 'numeric',
                                            hour: '2-digit',
                                            minute: '2-digit',
                                            second: '2-digit',
                                            hour12: true,
                                            timeZoneName: 'short',
                                        });
                                    })()}
                                </p>
                            )}
                        </div>
                        <div className="flex items-center">
                            <button onClick={handleRefresh} disabled={isRefreshing} className="p-2 mr-2 text-zinc-400 hover:text-white disabled:opacity-50 disabled:cursor-wait" title="Refresh List">
                                <FiRefreshCw className={isRefreshing ? 'animate-spin' : ''} />
                            </button>
                            <button onClick={onClose} className="text-zinc-500 hover:text-white"><IoMdClose size={24} /></button>
                        </div>
                    </div>

                    <div className="flex-grow overflow-auto space-y-3">
                        {loading ? <p>Loading...</p> : resources.map(resource => {
                            const isSelected = selectedResource?.id === resource.id;
                            const isExpanded = expandedResourceId === resource.id;
                            return (
                                <div key={resource.id} className={`border rounded-lg ${isSelected ? "border-[#5652CF]" : "border-zinc-700"}`}>
                                    {/* --- CHANGE 2: The entire header is now clickable to expand/collapse --- */}
                                    <div
                                        className={`flex justify-between items-center p-3 cursor-pointer ${isSelected ? "bg-zinc-800" : "bg-zinc-900"}`}
                                        onClick={() => handleToggleExpand(resource.id)}
                                    >
                                        {/* --- CHANGE 3: StopPropagation prevents the expand onClick from firing when clicking the radio/label --- */}
                                        <div className="flex items-center gap-3 flex-grow" onClick={(e) => e.stopPropagation()}>
                                            <input
                                                type="radio"
                                                id={`resource-${resource.id}`}
                                                name="resource-selection"
                                                checked={isSelected}
                                                onChange={() => handleSetResource(resource)}
                                                className="form-radio h-4 w-4 bg-zinc-700 border-zinc-600 text-[#5652CF] focus:ring-[#5652CF]"
                                            />
                                            <img src={resource.avatarUrl} alt={resource.name} className="h-8 w-8 rounded-full" />
                                            <div>
                                                <span className="font-medium text-zinc-200">{resource.name}</span>
                                                <a href={resource.url} target="_blank" rel="noopener noreferrer" className="block text-xs text-zinc-400 hover:text-blue-400 hover:underline truncate max-w-xs">
                                                    {resource.url}
                                                </a>
                                            </div>
                                        </div>
                                        <IoIosArrowDown className={`transition-transform text-zinc-400 ${isExpanded ? 'rotate-180' : ''}`} />
                                    </div>

                                    {isExpanded && (
                                        <div className="p-4 border-t border-zinc-700 bg-zinc-800">
                                            {/* --- CHANGE 1: "Select All" checkbox and selected count added --- */}
                                            <div className="flex justify-between items-center mb-3">
                                                <label className={`flex items-center gap-3 text-sm text-zinc-300 ${!isSelected ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}>
                                                    <input
                                                        type="checkbox"
                                                        onChange={handleSelectAll}
                                                        disabled={!isSelected}
                                                        checked={isSelected && selectedResource.projects.length > 0 && selectedProjectKeys.size === selectedResource.projects.length}
                                                        className="form-checkbox h-4 w-4 bg-zinc-700 border-zinc-600 rounded text-[#5652CF] focus:ring-[#5652CF] disabled:cursor-not-allowed"
                                                    />
                                                    Select All
                                                </label>
                                                {isSelected && <span className="text-xs text-zinc-400">{selectedProjectKeys.size} / {selectedResource.projects.length} selected</span>}
                                            </div>

                                            {!isSelected && (
                                                <div className="text-xs text-yellow-400 p-2 bg-yellow-900/50 rounded-md mb-3">
                                                    Select this resource to enable project selection.
                                                </div>
                                            )}
                                            <ul className={`space-y-4 max-h-60 overflow-y-auto ${!isSelected ? 'opacity-50' : ''}`}>
                                                {resource.projects.map(project => (
                                                    <li key={project.key} className="p-2 rounded-md">
                                                        <div className="flex items-center gap-3">
                                                            <input
                                                                type="checkbox"
                                                                id={project.key}
                                                                checked={selectedProjectKeys.has(project.key)}
                                                                onChange={() => handleToggleProject(project.key)}
                                                                disabled={!isSelected}
                                                                className="form-checkbox h-5 w-5 bg-zinc-900 border-zinc-600 text-[#5652CF] focus:ring-[#5652CF] disabled:cursor-not-allowed"
                                                            />
                                                            <label htmlFor={project.key} className={`text-zinc-300 ${!isSelected ? 'cursor-not-allowed' : 'cursor-pointer'}`}>
                                                                {project.name} ({project.key})
                                                            </label>
                                                        </div>
                                                        {project.boards && project.boards.length > 0 && (
                                                            <div className="mt-2 pl-8">
                                                                <ul className="flex flex-wrap gap-2">
                                                                    {project.boards.map(board => (
                                                                        <li key={board.id} className="bg-zinc-700 text-zinc-300 text-xs px-2 py-1 rounded-full">
                                                                            {board.name}
                                                                        </li>
                                                                    ))}
                                                                </ul>
                                                            </div>
                                                        )}
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}
                                </div>
                            )
                        })}
                    </div>

                    <div className="mt-6 flex justify-between items-center pt-4 border-t border-zinc-700">
                        <button
                            onClick={() => setIsSyncModalOpen(true)}
                            className="bg-zinc-700 hover:bg-zinc-600 text-white font-bold py-2 px-4 rounded-md transition-colors"
                        >
                            Manage Synchronization
                        </button>
                        <button
                            onClick={handleConfirmSelection}
                            disabled={!selectedResource || confirmLoading}
                            className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-md disabled:opacity-50 transition-colors"
                        >
                            {confirmLoading ? "Saving..." : "Save Configuration"}
                        </button>
                    </div>
                </div>
            </div>

            <JiraSynchronizationModal
                isOpen={isSyncModalOpen}
                onClose={() => setIsSyncModalOpen(false)}
            />
        </>
    );
};

export default JiraResourceModal;